import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { adminApi } from '../services/api';

interface DashboardStats {
  users: {
    total: number;
    active: number;
    suspended: number;
  };
  reviews: {
    total: number;
    active: number;
    deleted: number;
  };
  blog_posts: {
    total: number;
    published: number;
    draft: number;
  };
  reports: {
    total: number;
    pending: number;
    processed: number;
  };
  admins: {
    count: number;
  };
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getDashboard();
      setStats(response.dashboard);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) return null;

  const StatCard: React.FC<{ title: string; data: any }> = ({ title, data }) => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {Object.entries(data).map(([key, value]) => (
          <Box key={key} display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              {key === 'total' && '総数'}
              {key === 'active' && 'アクティブ'}
              {key === 'suspended' && '停止中'}
              {key === 'deleted' && '削除済み'}
              {key === 'published' && '公開済み'}
              {key === 'draft' && '下書き'}
              {key === 'pending' && '未処理'}
              {key === 'processed' && '処理済み'}
              {key === 'count' && '管理者数'}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {String(value)}
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        管理ダッシュボード
      </Typography>
      
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          mb: 3
        }}
      >
        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <StatCard title="ユーザー" data={stats.users} />
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <StatCard title="口コミ" data={stats.reviews} />
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <StatCard title="ブログ記事" data={stats.blog_posts} />
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <StatCard title="通報" data={stats.reports} />
        </Box>
      </Box>
      
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          mt: 3
        }}
      >
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                システム概要
              </Typography>
              <Typography variant="body2" color="text.secondary">
                管理者数: {stats.admins.count}名
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                総ユーザー数: {stats.users.total}名
              </Typography>
              <Typography variant="body2" color="text.secondary">
                総投稿数: {stats.reviews.total}件
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                要注意項目
              </Typography>
              {stats.reports.pending > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  未処理の通報が {stats.reports.pending} 件あります
                </Alert>
              )}
              {stats.users.suspended > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  停止中のユーザーが {stats.users.suspended} 名います
                </Alert>
              )}
              {stats.reports.pending === 0 && stats.users.suspended === 0 && (
                <Typography variant="body2" color="text.secondary">
                  特に問題はありません
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
